import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { Box, CardContent, CardHeader, Tab } from "@mui/material";
import { MprcCard, MprcIconFa } from "components";
import { MprcContainer } from "components/MprcContainer";
import { TFunction } from "i18next";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditUserAdditionalInfoBox, IUserAdditonalInfoBoxData } from "views/foundation/user/Home/EditUser/EditUserAdditionalInfoBox";
import { MprcBarTabs } from "../../../../../components/MprcBarTabs";
import { EditUserManagementInfoBox, IUserManagementInfoBoxData } from "./EditUserManagementInfoBox";
import { EditUserPersonInfoBox, IUserPersonalInfoBoxData } from "./EditUserPersonInfoBox";

////////////////////////////////////////////////////////////////////////////

export type UserTabOptions = "managementInfo" | "personalInfo" | "additionalInfo";

interface IData extends IUserManagementInfoBoxData, IUserPersonalInfoBoxData, IUserAdditonalInfoBoxData {
}
interface IProps {
    viewData: IData;
    onFormValidation: (valid: boolean) => void;
}

interface IBoxValidations {
    managementInfoBox?: boolean;
    personalInfoBox?: boolean;
    additionalInfoBox?: boolean;
}

////////////////////////////////////////////////////////////////////////////

///
function getEmptyValidations(): IBoxValidations {
    return { managementInfoBox: undefined, personalInfoBox: undefined, additionalInfoBox: undefined };
}

///
function getTabIcon(show: boolean, t: TFunction) {
    return show === true ? (
        <MprcIconFa
            icon={faTriangleExclamation}
            color="warning"
            fixedWidth
            title={t("User.Edit.AlertInvalidValuesTooltip")}
        />
    ) : (
        <div>{/* Deixa em branco, para manter o mesmo height de quando o ícone é exibido */}</div>
    );
}

////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Usuário - Formulário secundário
 * @param props
 */
export const EditUserSecondaryBox: React.FC<IProps> = (props) => {
    const {
        viewData,
        onFormValidation } = props;

    const { t } = useTranslation();

    const [currentTab, setTab] = useState<UserTabOptions>("managementInfo");
    const [boxValidations, setBoxValidations] = useState<IBoxValidations>(getEmptyValidations());

    ////////////////////////////////////////////////////////////////////////////
    useEffect(() => {
        onFormValidation(Object.values(boxValidations).filter((x) => x === false || x === undefined).length == 0);
    }, [boxValidations]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <MprcContainer>
            <MprcCard>
                <CardHeader
                    title={
                        <MprcBarTabs value={currentTab} onChange={(v, selected) => setTab(selected)} variant="fullWidth">
                            <Tab
                                value="managementInfo"
                                label={t("User.Edit.ManagementTitle")}
                                icon={getTabIcon(boxValidations.managementInfoBox === false && boxValidations.managementInfoBox != undefined && currentTab != "managementInfo", t)}
                                iconPosition="end"
                            />
                            <Tab
                                value="personalInfo"
                                label={t("User.Edit.PersonalTitle")}
                                icon={getTabIcon(boxValidations.personalInfoBox === false && boxValidations.personalInfoBox != undefined && currentTab != "personalInfo", t)}
                                iconPosition="end"
                            />
                            <Tab
                                value="additionalInfo"
                                label={t("User.Edit.AdditionalTitle")}
                                icon={getTabIcon(boxValidations.additionalInfoBox === false && boxValidations.additionalInfoBox != undefined && currentTab != "additionalInfo", t)}
                                iconPosition="end"
                            />
                        </MprcBarTabs>
                    }
                ></CardHeader>
                <CardContent>
                    <Box sx={currentTab === "managementInfo" ? { visibility: "visible" } : { visibility: "hidden", maxHeight: 0 }}>
                        <EditUserManagementInfoBox
                            viewData={{
                                admissionDate: viewData.admissionDate,
                                approver: viewData.approver,
                                customFieldsCorporate: viewData.customFieldsCorporate,
                                dismissedDate: viewData.dismissedDate,
                                manager: viewData.manager,
                                secondaryManagers: viewData.secondaryManagers,
                                unitTypes: viewData.unitTypes,
                                userId: viewData.userId
                            }}
                            onFormValidation={(valid) =>
                                setBoxValidations((state) => {
                                    return { ...state, managementInfoBox: valid };
                                })
                            }
                        />
                    </Box>
                    <Box sx={currentTab === "personalInfo" ? { visibility: "visible" } : { visibility: "hidden", maxHeight: 0 }}>
                        <EditUserPersonInfoBox
                            viewData={{
                                birthDate: viewData.birthDate,
                                customFieldsPersonal: viewData.customFieldsPersonal,
                                phones: viewData.phones,
                                socialNetworks: viewData.socialNetworks,
                                userId: viewData.userId,
                            }}
                            onFormValidation={(valid) =>
                                setBoxValidations((state) => {
                                    return { ...state, personalInfoBox: valid };
                                })
                            }
                        />
                    </Box>
                    <Box sx={currentTab === "additionalInfo" ? { visibility: "visible" } : { visibility: "hidden", maxHeight: 0 }}>
                        <EditUserAdditionalInfoBox
                            viewData={{
                                customFieldsAdditional: viewData.customFieldsAdditional,
                                customFieldsConcealed: viewData.customFieldsConcealed,
                                tags: viewData.tags,
                            }}
                            onFormValidation={(valid) =>
                                setBoxValidations((state) => {
                                    return { ...state, additionalInfoBox: valid };
                                })
                            }
                        />
                    </Box>
                </CardContent>
            </MprcCard>
        </MprcContainer>
    );
};
